import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { isNativeRuntime } from "@/lib/runtime";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import SetupPasswordPage from "@/pages/SetupPasswordPage";
import SecurityPage from "@/pages/SecurityPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import GroupDetail from "@/pages/GroupDetail";
import DailySummary from "@/pages/DailySummary";
import History from "@/pages/History";
import ImportPage from "@/pages/ImportPage";
import BillingPage from "@/pages/BillingPage";
import InvoicesPage from "@/pages/InvoicesPage";
import SettingsPage from "@/pages/SettingsPage";
import SupportPage from "@/pages/SupportPage";

// Admin Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";

const BrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 4h14v4.2L8.7 20H5.2l9.1-10.4H5V4z" />
  </svg>
);

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      return;
    }
    
    if (!loading && !user) {
      checkAuth().then((authenticated) => {
        if (!authenticated) {
          navigate("/login", { replace: true });
        }
      });
    }
  }, [user, loading, checkAuth, navigate, location.state]);

  // Check admin access
  useEffect(() => {
    if (adminOnly && user && user.role !== 'admin') {
      navigate('/app', { replace: true });
    }
  }, [adminOnly, user, navigate]);

  useEffect(() => {
    if (!user || adminOnly || user.role === 'admin') return;
    if (user.requires_password_setup && location.pathname !== '/app/setup-password') {
      navigate('/app/setup-password', { replace: true });
    }
    if (!user.requires_password_setup && location.pathname === '/app/setup-password') {
      navigate('/app', { replace: true });
    }
  }, [user, adminOnly, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={isNativeRuntime() ? <NativeEntryRoute /> : <LandingPage />} />
      <Route path="/pessoal" element={<LandingPage variant="personal" />} />
      <Route path="/business" element={<LandingPage variant="business" />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/security" element={<SecurityPage />} />
      
      {/* Protected routes */}
      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/app/groups/:groupId" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
      <Route path="/app/daily" element={<ProtectedRoute><DailySummary /></ProtectedRoute>} />
      <Route path="/app/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
      <Route path="/app/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
      <Route path="/app/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/app/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/app/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/app/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/app/setup-password" element={<ProtectedRoute><SetupPasswordPage /></ProtectedRoute>} />

      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/support" element={<ProtectedRoute adminOnly><AdminSupport /></ProtectedRoute>} />
      <Route path="/admin/users/:userId" element={<ProtectedRoute adminOnly><AdminUserDetail /></ProtectedRoute>} />
    </Routes>
  );
}

function NativeEntryRoute() {
  const { user, checkAuth, loading } = useAuth();
  const navigate = useNavigate();
  const [splashComplete, setSplashComplete] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSplashComplete(true);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    checkAuth(true);
  }, [checkAuth]);

  useEffect(() => {
    if (!splashComplete || loading) return;

    if (user?.role === 'admin') {
      navigate('/admin', { replace: true });
      return;
    }

    if (user) {
      navigate('/app', { replace: true });
      return;
    }

    navigate('/login', { replace: true });
  }, [splashComplete, loading, user, navigate]);

  return (
    <div className="mobile-safe-shell bg-zinc-950 text-white flex items-center justify-center">
      <div className="mobile-safe-content max-w-sm text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-sky-500 to-violet-600 shadow-[0_22px_60px_-18px_rgba(14,165,233,0.65)]">
          <BrandIcon className="h-10 w-10 text-white" />
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Zapt Resumo</h1>
        <p className="mt-2 text-xs font-semibold uppercase tracking-[0.28em] text-sky-300">Aplicativo oficial</p>
        <div className="mx-auto mt-8 h-1.5 w-24 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full w-full rounded-full bg-gradient-to-r from-sky-400 via-violet-400 to-emerald-400"
            style={{ animation: 'native-splash-load 5s linear forwards' }}
          />
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
